import {DataTypesEnum} from "../enums/dataTypesEnum";
import {AttributeInterface} from "../Interfaces/AttributeInterface";
import {BusinessObjectInterface} from "../Interfaces/BusinessObjectInterface";
import {Attribute} from "./Attribute";
import {Relationship} from "./Relationship";
import {RelationshipInterface} from "../Interfaces/RelationshipInterface";
import {AttributeTypeEnum} from "../enums/AttributeTypeEnum";

export class BusinessObject implements BusinessObjectInterface {
    readonly id: number;
    private _name: string;
    private _systemRequired: boolean;
    private _description: string;
    private _instancesCount: number;
    private _tags: Set<string> = new Set();
    private _attributes: Map<number, AttributeInterface> = new Map();
    private _relationships: Map<number, RelationshipInterface> = new Map();
    private _uniqueName: boolean;
    private _inferenceTemp: boolean;

    constructor(id: number, name: string, description: string, instancesCount: number, tags: string[], attributes: AttributeInterface[] | Map<number, AttributeInterface>, relationships: RelationshipInterface[] | Map<number, RelationshipInterface>, systemRequired: boolean, inferenceTemp: boolean, uniqueName?: boolean) {
        this.id = id;
        this._name = name;
        this._description = description;
        this._instancesCount = instancesCount;
        this.addAttributes(attributes);
        this.addRelationships(relationships);
        this.systemRequired = systemRequired;
        this._inferenceTemp = inferenceTemp;
        this._uniqueName = uniqueName;
    }

    get inferenceTemp(): boolean {
        return this._inferenceTemp;
    }

    set inferenceTemp(value: boolean) {
        this._inferenceTemp = value;
    }

    get uniqueName(): boolean {
        return this._uniqueName;
    }

    set uniqueName(value: boolean) {
        this._uniqueName = value;
    }

    get systemRequired(): boolean {
        return this._systemRequired;
    }

    set systemRequired(value: boolean) {
        this._systemRequired = value;
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get description(): string {
        return this._description;
    }

    set description(value: string) {
        this._description = value;
    }

    get instancesCount(): number {
        return this._instancesCount;
    }

    set instancesCount(value: number) {
        this._instancesCount = value;
    }

    get tags(): Set<string> {
        return this._tags;
    }

    set tags(value: Set<string>) {
        this._tags = value;
    }

    get attributes(): Map<number, AttributeInterface> {
        return this._attributes;
    }

    set attributes(value: Map<number, AttributeInterface>) {
        this._attributes = value;
    }

    get relationships(): Map<number, RelationshipInterface> {
        return this._relationships;
    }

    set relationships(value: Map<number, RelationshipInterface>) {
        this._relationships = value;
    }

    addAttributes(attributes: AttributeInterface[] | Map<number, AttributeInterface>): void {
        if (attributes instanceof Array) {
            attributes.forEach(attribute => {
                this._attributes.set(attribute.id, new Attribute(attribute.id, attribute.name, attribute.description, attribute.unitOfMeasure, attribute.type,
                    attribute.minLength, attribute.maxLength, attribute.maxDigitsLeftOfDecimal, attribute.minDigitsRightOfDecimal, attribute.regexValidation,
                    attribute.uiSection, attribute.prefix, attribute.suffix, attribute.receiveNotifications, attribute.boClass, attribute.attributeValues, attribute.required, attribute.systemRequired,
                    attribute.defaultValue, attribute.lookupAttributeId, attribute.createRelationships, attribute.lookupRelTypeId))
            })
        } else if (attributes instanceof Map) {
            this._attributes = attributes;
        }
    }

    addRelationships(relationships: RelationshipInterface[] | Map<number, RelationshipInterface>): void {
        if (relationships instanceof Array) {
            relationships.forEach(relationship => {
                this._relationships.set(parseInt(relationship.id.toString(), 10), new Relationship(relationship.id, relationship.name, relationship.inward, relationship.outward, relationship.fromBoClass, relationship.toBoClass, relationship.systemRequired));
            })
        } else if (relationships instanceof Map) {
            this._relationships = relationships;
        }
    }

    setTags(tags: string[]): void {
        tags.forEach(tag => {
            this._tags.add((tag));
        })
    }

    hasAttribute(arg: number | string): boolean {
        switch (typeof arg) {
            case "number": {
                return this._attributes.has(arg);
            }
            case "string": {
                const attributesArray = Array.from(this._attributes.values());
                for (let i = 0; i < attributesArray.length; i++) {
                    if (attributesArray[i].name.trim().toLowerCase() === arg.trim().toLowerCase()) {
                        return true
                    }
                }
                return false;
            }
        }
    }

    hasRelationship(arg: number, name?: string): boolean {
        if (name) {

        } else {
            return this._relationships.has(arg);
        }
    }

    addAttribute(attribute: AttributeInterface): void {
        this._attributes.set(attribute.id, attribute);
    }

    addRelationship(relationship: RelationshipInterface): void {
        this._relationships.set(relationship.id, new Relationship(relationship.id, relationship.name, relationship.inward, relationship.outward, relationship.fromBoClass, relationship.toBoClass, relationship.systemRequired));
    }

    deleteAttribute(id: number): void {
        this._attributes.delete(id);
    }

    deleteRelationship(id: number): void {
        this._relationships.delete(id);
    }

    getAttribute(id: number): AttributeInterface {
        return this._attributes.get(id);
    }

    getRelationship(id: number): RelationshipInterface {
        return this._relationships.get(id);
    }

    isErasable(): { erasable: boolean; msg: string | null } {
        const attributes = <AttributeInterface[]>this.getAttributes(DataTypesEnum.ARRAY);
        for (let attribute of attributes) {
            const isErasable = attribute.isErasable();
            if (!isErasable.erasable) {
                return {
                    erasable: false,
                    msg: `You cannot delete this type, because you have the attribute "${attribute.name}" set as system attribute.To delete this type, you must first remove the "System" setting from the attribute.`
                };
            }
        }

        const relationshipsArray = Array.from(this.relationships.values());
        for (let relationship of relationshipsArray) {
            const isErasable = relationship.isErasable();
            if (!isErasable.erasable) {
                return {
                    erasable: false,
                    msg: `You cannot delete this type, because you have relationship "${relationship.name}" set as system relationship.To delete this type, you must first remove the "System" setting from the relationship.`
                }
            }
        }

        if (this.systemRequired) {
            return {erasable: false, msg: "You cannot delete this type, because it is set as system type."};
        }
        return {erasable: true, msg: null};
    }

    equal(businessObject: BusinessObjectInterface): boolean {
        if (!businessObject) {
            return false;
        }
        if (this._name !== businessObject.name) {
            return false;
        }
        if (this._description !== businessObject.description) {
            return false;
        }
        if (this.systemRequired !== businessObject.systemRequired) {
            return false;
        }
        if (this._uniqueName !== businessObject.uniqueName) {
            return false;
        }
        return this._instancesCount === businessObject.instancesCount;
    }

    clone(): BusinessObject {
        return new BusinessObject(this.id, this._name, this._description, this._instancesCount, this.getTags(),
            this.getAttributes(DataTypesEnum.ARRAY), this.getRelationships(DataTypesEnum.ARRAY), this._systemRequired,
            this._inferenceTemp, this._uniqueName);
    }

    getTags(): string[] {
        return Array.from(this._tags);
    }

    getAttributes(arg: string): AttributeInterface[] | Map<number, AttributeInterface> {
        switch (arg) {
            case DataTypesEnum.ARRAY: {
                return Array.from(this._attributes.values());
            }
            case DataTypesEnum.MAP: {
                return this._attributes;
            }
        }
    }

    getRelationships(arg: string): RelationshipInterface[] | Map<number, RelationshipInterface> {
        switch (arg) {
            case DataTypesEnum.ARRAY: {
                return Array.from(this._relationships.values());
            }
            case DataTypesEnum.MAP: {
                return this._relationships;
            }
        }
    }

    getInstanceCount(): number {
        return this._instancesCount;
    }

    static enableButton(id: string, msg?: string) {
        $(`#${id}`)
            .prop("disabled", false)
            .attr("title", msg)
        ;
    }

    static disableButton(id: string, msg?: string) {
        const $target = $(`#${id}`);
        $target
            .prop("disabled", true)
            .attr("title", msg);
    }

    isValid(data = new Map()): { valid: boolean, data: Map<string, { valid: boolean, msg: string }> } {
        data.set("name", {
            valid: this.name.trim() !== "",
            msg: this.name.trim() === "" ? "Business object name can't be an empty string." : null
        });
        data.set("description", {
            valid: this.description.trim() !== "",
            msg: this.description.trim() === "" ? "Business object description can't be an empty string." : null
        });
        return {valid: !Array.from(data.values()).some(item => item.valid === false), data}
    }

    checkAttributesBeforeSave(attribute: Attribute): { valid: boolean; data: Map<string, { valid: boolean, msg: string }> } {
        const result = attribute.isValid();
        if (this.hasAttribute(attribute.id)) {
            const attr = this.getAttribute(attribute.id);
            const equal = attr.equal(attribute);
            result.data.set("equal", {
                valid: !equal,
                msg: equal ? "There are no changes." : null
            })
        }
        if (result.data.get("name").valid) {
            const exists = this.hasAttributeByNameExceptId(attribute.name, attribute.id);
            if (attribute.name === undefined || attribute.name === null) {
                result.data.set("name", {
                    valid: false,
                    msg: 'Attribute name cannot be empty'
                })
            } else {
                result.data.set("name", {
                    valid: !exists,
                    msg: exists ? `A business object class with name: "${attribute.name}" already exists.` : null
                })
            }
        }
        if (attribute.type === AttributeTypeEnum.BOOLEAN && attribute.required && attribute.defaultValue === 'n/a') {
            result.data.set("defaultValue", {
                valid: false,
                msg: "The default value of required boolean attribute can not be 'n/a'"
            })
        }
        result.valid = !Array.from(result.data.values()).some(item => item.valid === false);
        return result
    }

    checkRelationshipsBeforeSave(relationship: Relationship): { valid: boolean; data: Map<string, { valid: boolean, msg: string }> } {
        const result = relationship.isValid();
        if (this.hasRelationship(relationship.id)) {
            const rel = this.getRelationship(relationship.id);
            const equal = rel.equal(relationship);
            result.data.set("equal", {
                valid: !equal,
                msg: equal ? "There are no changes." : null
            })
        }
        if (result.data.get("name").valid) {
            const isUnique = this.isUnique(relationship);
            result.data.set("name", {
                valid: isUnique,
                msg: !isUnique ? `Relationship from ${relationship.fromBoClass.name} to ${relationship.toBoClass.name} named "${relationship.name}" already exists.` : null
            })
        }
        result.valid = !Array.from(result.data.values()).some(item => item.valid === false);
        return result
    }

    getRelationshipByToBocIdAndRelName(id: number, name: string): RelationshipInterface {
        const relationshipsArray = <RelationshipInterface[]>this.getRelationships(DataTypesEnum.ARRAY);
        for (let i = 0; i < relationshipsArray.length; i++) {
            const relationship = relationshipsArray[i];
            if (relationship.toBoClass.id === id && relationship.name === name) {
                return relationship
            }
        }
        return undefined;
    }

    hasAttributeByNameExceptId(name: string, id: number): boolean {
        const attributesArray = Array.from(this._attributes.values());
        for (let i = 0; i < attributesArray.length; i++) {
            if (name === null || name === undefined) {
                return false;
            }
            if (attributesArray[i].name.trim().toLowerCase() === name.trim().toLowerCase() && attributesArray[i].id !== id) {
                return true;
            }
        }
        return false;
    }

    isUnique(relationship: RelationshipInterface): boolean {
        const relationshipsArray = <RelationshipInterface[]>this.getRelationships(DataTypesEnum.ARRAY);
        for (let i = 0; i < relationshipsArray.length; i++) {
            if (relationshipsArray[i].toBoClass.id === relationship.toBoClass.id && relationship.fromBoClass.id === relationshipsArray[i].fromBoClass.id && relationshipsArray[i].name.trim().toLowerCase() === relationship.name.trim().toLowerCase() && relationshipsArray[i].id !== relationship.id) {
                return false;
            }
        }
        return true;
    }
}



// WEBPACK FOOTER //
// ./app/components/bo-core/models/BusinessObject.ts